import { useTranslation } from 'react-i18next';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { useHreflangAlternates, useSeoMeta } from '@/hooks/useSeoMeta';
import { DimensionsGrid } from '@/features/marketing';
import { localizePath } from '@/i18n/paths';

interface Props {
  lang: 'en' | 'ru';
}

/** Canonical host — must match the origin build-sitemap.mjs advertises. */
const PROD_ORIGIN = 'https://lodestar.place';

/**
 * Absolute URL of the topics index in `lang`.  Built through
 * `localizePath()` so the `/ru` prefix comes from one place — a
 * hand-concatenated prefix here would drift from the router and from
 * build-sitemap.mjs the moment a third language ships.
 */
function topicsIndexUrl(lang: 'en' | 'ru'): string {
  return `${PROD_ORIGIN}${localizePath('/topics', lang)}`;
}

export default function TopicsIndexPage({ lang }: Props) {
  const { t } = useTranslation();
  useDocumentLang();

  // The canonical used to be the relative string '/topics' on BOTH language
  // twins, so /ru/topics claimed the English URL as its canonical and
  // de-indexed itself.  It must be the absolute URL of this very page, and
  // the pair must declare each other via hreflang — the sitemap already
  // advertises them as alternates.
  useSeoMeta({
    title: t('topics.index.meta.title'),
    description: t('topics.index.meta.description'),
    canonical: topicsIndexUrl(lang),
    locale: lang === 'ru' ? 'ru_RU' : 'en_US',
  });
  useHreflangAlternates([
    { hreflang: 'en', href: topicsIndexUrl('en') },
    { hreflang: 'ru', href: topicsIndexUrl('ru') },
    { hreflang: 'x-default', href: topicsIndexUrl('en') },
  ]);

  return (
    <main id="main">
      <div className="mx-auto max-w-5xl px-6 pt-12 pb-4">
        <h1 className="font-display text-4xl font-medium tracking-tight text-ink-900 sm:text-5xl">
          {t('topics.index.title', { defaultValue: 'Browse by Topic' })}
        </h1>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink-500">
          {t('topics.index.sub', {
            defaultValue:
              'Pick a dimension to see how 31 countries compare — published guides first, full coverage coming.',
          })}
        </p>
      </div>
      <DimensionsGrid />
    </main>
  );
}
